import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Warning12Filled,
    ErrorCircle12Filled,
    Circle12Regular,
    SubtractCircle12Regular,
    Dismiss12Regular,
    Checkmark12Regular,
} from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../../Observations';
import { PostInformationRequestSidebar } from '../../IRL';
import AskAiQuickCreateSidebar from '../AskAiQuickCreateSidebar';
import { saveInsightsModuleToJournal, buildInsightsItemJournalBody } from '../../../utils/askLensJournal';
import { buildNavigatePath } from '../../../utils/responseActionHandlers';
import {
    filterSupportedInsightsActions,
    firstSupportedInsightsAction,
    isInsightsReadOnlyBlocked,
} from '../../../utils/intelligenceInsightsActions';
import InsightsModuleChrome from './InsightsModuleChrome';
import { useInsightsReadOnly } from './InsightsReadOnlyContext';
import HighlightsEvidencePanel from './HighlightsEvidencePanel';
import ModuleEmptyState from './ModuleEmptyState';
import styles from './intelligenceInsightsModules.module.css';
import drawerStyles from './intelligenceInsightsHighlights.module.css';

function titleCase(value) {
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

function riskPrefill(risk) {
    const detail = risk?.detail || {};
    return [
        risk?.title,
        risk?.body,
        detail.whyRisk,
        detail.recommendedResponse,
    ]
        .filter(Boolean)
        .join('\n\n');
}

function severityClass(severity) {
    const s = String(severity || '').toLowerCase();
    if (s === 'critical') return styles.severityCritical;
    if (s === 'high') return styles.severityHigh;
    if (s === 'low') return styles.severityLow;
    return styles.severityMedium;
}

function SeverityIcon({ severity }) {
    const s = String(severity || '').toLowerCase();
    if (s === 'critical') {
        return <ErrorCircle12Filled style={{ color: '#ef4444' }} aria-hidden />;
    }
    if (s === 'high') {
        return <Warning12Filled style={{ color: '#f59e0b' }} aria-hidden />;
    }
    if (s === 'low') {
        return <SubtractCircle12Regular style={{ color: '#9ca3af' }} aria-hidden />;
    }
    return <Circle12Regular style={{ color: '#9ca3af' }} aria-hidden />;
}

function statusChipClass(statusKey) {
    const key = String(statusKey || '').toLowerCase();
    if (key === 'in_progress' || key === 'in progress') return styles.statusChipProgress;
    return styles.statusChip;
}

function relatedLabel(item) {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.label || item.id || '';
}

function isRaidLinked(detail = {}) {
    const raid = detail.raidRelationship || {};
    return Boolean(raid.linked && raid.raidItemId);
}

function buildRaidDrawerAction(detail) {
    if (isRaidLinked(detail)) {
        return {
            key: 'open_raid_item',
            label: 'Open RAID',
            target: {
                type: 'raid',
                id: detail.raidRelationship.raidItemId,
                raid_item_id: detail.raidRelationship.raidItemId,
                route: '/raid',
            },
        };
    }
    return { key: 'add_to_raid', label: 'Add to RAID' };
}

function RiskDetailDrawer({ open, risk, onClose, onAction, onOpenModule }) {
    if (!open || !risk) return null;
    const detail = risk.detail || {};
    const evidenceCount =
        detail.evidenceCount ??
        (risk.evidenceReferenceIds || []).length;
    const viewEvidenceAction =
        (risk.actions || []).find(
            a => a.key === 'view_evidence' || a.key === 'open_evidence_panel',
        ) || { key: 'view_evidence', label: 'View evidence' };
    const raidAction = buildRaidDrawerAction(detail);
    const impactText = Array.isArray(detail.potentialImpact)
        ? detail.potentialImpact.filter(Boolean).join('. ')
        : detail.potentialImpact || '';

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}
            style={{ width: 480, maxWidth: '100vw', background: '#fff' }}>
            <DrawerBody className={drawerStyles.detailDrawerBody} style={{ width: 480, background: '#fff' }}>
                <div className={styles.swotDetailRoot}>
                    <div className={styles.swotDetailHeader}>
                        <div className={styles.swotDetailHeaderText}>
                            <h2 className={styles.riskDetailTitle}>{risk.title}</h2>
                            <div className={styles.chipRow} style={{ marginTop: 6 }}>
                                <span
                                    className={`${styles.severityCell} ${severityClass(
                                        risk.severity,
                                    )}`}>
                                    <SeverityIcon severity={risk.severity} />
                                    {titleCase(risk.severity)}
                                </span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className={drawerStyles.drawerClose}
                            onClick={onClose}
                            aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>

                    <div className={styles.swotDetailBody}>
                        {typeof risk.body === 'string' && risk.body ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Description</p>
                                <p className={styles.riskDetailBody}>{risk.body}</p>
                            </section>
                        ) : null}

                        {detail.whyRisk ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Why it is a risk</p>
                                <p className={styles.riskDetailBody}>{detail.whyRisk}</p>
                            </section>
                        ) : null}

                        {impactText ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Potential impact</p>
                                <p className={styles.riskDetailBody}>{impactText}</p>
                            </section>
                        ) : null}

                        {typeof detail.likelihood === 'string' && detail.likelihood ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Likelihood</p>
                                <p className={styles.riskDetailMuted}>{detail.likelihood}</p>
                            </section>
                        ) : null}

                        {detail.earlyWarningSignals?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Early-warning signals</p>
                                <ul className={styles.riskSignalList}>
                                    {detail.earlyWarningSignals.map(item => (
                                        <li key={item} className={styles.riskSignalItem}>
                                            <Checkmark12Regular
                                                className={styles.riskSignalCheck}
                                                aria-hidden
                                            />
                                            <span>{typeof item === 'string' ? item : ''}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ) : null}

                        {typeof detail.recommendedResponse === 'string' &&
                        detail.recommendedResponse ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Recommended response</p>
                                <div className={styles.swotDetailResponse}>
                                    {detail.recommendedResponse}
                                </div>
                            </section>
                        ) : null}

                        {detail.relatedFindings?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Related findings</p>
                                <div className={styles.chipRow}>
                                    {detail.relatedFindings.map(item => {
                                        const label = relatedLabel(item);
                                        const id = typeof item === 'object' ? item.id : item;
                                        return (
                                            <button
                                                key={id || label}
                                                type="button"
                                                className={styles.riskRelatedChip}
                                                onClick={() => {
                                                    onClose?.();
                                                    onOpenModule?.(
                                                        item?.moduleKey || 'key_findings',
                                                        { findingId: id },
                                                    );
                                                }}>
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        ) : null}

                        {detail.relatedEvidenceMatters?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Related evidence matters</p>
                                <div className={styles.chipRow}>
                                    {detail.relatedEvidenceMatters.map(item => {
                                        const label = relatedLabel(item);
                                        const id = typeof item === 'object' ? item.id : item;
                                        return (
                                            <button
                                                key={id || label}
                                                type="button"
                                                className={styles.riskRelatedChip}
                                                onClick={() => {
                                                    onClose?.();
                                                    onOpenModule?.(
                                                        item?.moduleKey || 'evidence_follow_ups',
                                                        { evidenceMatterId: id },
                                                    );
                                                }}>
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        ) : null}

                        {detail.evidenceCoverage ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Evidence coverage</p>
                                <p className={styles.riskDetailBody}>{detail.evidenceCoverage}</p>
                            </section>
                        ) : null}

                        <section className={styles.swotDetailSection}>
                            <p className={styles.swotDetailLabel}>Evidence</p>
                            <div className={styles.swotDetailEvidenceRow}>
                                <span className={styles.swotDetailEvidenceCount}>
                                    {evidenceCount
                                        ? `${evidenceCount} item${evidenceCount === 1 ? '' : 's'}`
                                        : 'No linked items'}
                                </span>
                                <button
                                    type="button"
                                    className={styles.swotDetailEvidenceLink}
                                    onClick={() => onAction?.(viewEvidenceAction, risk)}>
                                    View evidence
                                </button>
                            </div>
                        </section>

                        <section className={styles.swotDetailSection}>
                            <p className={styles.swotDetailLabel}>RAID relationship</p>
                            {isRaidLinked(detail) ? (
                                <p className={styles.riskDetailMuted}>
                                    Linked to RAID record
                                    {detail.raidRelationship.workflowStatus
                                        ? ` · ${titleCase(detail.raidRelationship.workflowStatus)}`
                                        : ''}
                                </p>
                            ) : (
                                <p className={styles.riskDetailMuted}>Not linked to RAID</p>
                            )}
                        </section>

                        <section className={styles.swotDetailSection}>
                            <p className={styles.swotDetailLabel}>Actions</p>
                            <div className={styles.swotDetailActions}>
                                <button
                                    type="button"
                                    className={styles.swotDetailGhostBtn}
                                    onClick={() => onAction?.(raidAction, risk)}>
                                    {raidAction.label}
                                </button>
                                <button
                                    type="button"
                                    className={styles.swotDetailGhostBtn}
                                    onClick={() =>
                                        onAction?.(
                                            { key: 'save_to_journal', label: 'Save to Journal' },
                                            risk,
                                        )
                                    }>
                                    Save to Journal
                                </button>
                            </div>
                        </section>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

const IntelligenceInsightsRisksSignals = ({
    projectId,
    data,
    loading,
    error,
    onAskAi,
    onExport,
    exportEnabled = false,
    onOpenModule,
    onGenerateAnalysis,
    onGoToOverview,
    onNewEvidenceAction,
    onRetry,
    generating = false,
    historicalVersion = null,
    onExitHistoricalView = null,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const notesState = useSelector(state => state?.dashboard?.notes);
    const readOnly = useInsightsReadOnly();

    const filterDefaults = data?.filters || {};
    const [severityFilter, setSeverityFilter] = useState(filterDefaults.severity || 'all');
    const [scopeFilter, setScopeFilter] = useState(filterDefaults.scope || 'all');
    const [statusFilter, setStatusFilter] = useState(filterDefaults.status || 'all');
    const [confidenceFilter, setConfidenceFilter] = useState(filterDefaults.confidence || 'all');
    const [detailRisk, setDetailRisk] = useState(null);
    const [evidenceTarget, setEvidenceTarget] = useState(null);
    const [sidebarContent, setSidebarContent] = useState('');
    const [showIrlSidebar, setShowIrlSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);
    const [showObsSidebar, setShowObsSidebar] = useState(false);

    const closeOverlays = () => {
        setDetailRisk(null);
        setEvidenceTarget(null);
    };

    const handleAction = async (action, risk = null) => {
        if (!action || isInsightsReadOnlyBlocked(readOnly, action)) return;
        const key = action.key || action.actionType || action.action_type;
        const target = action.target || {};
        const text = riskPrefill(risk);

        if (key === 'view_details') {
            setEvidenceTarget(null);
            setDetailRisk(risk);
            return;
        }
        if (key === 'view_evidence' || key === 'open_evidence_panel') {
            setDetailRisk(null);
            const objectType = target.object_type || target.type || (risk ? 'risk' : null);
            const objectId = target.object_id || target.id || risk?.id || null;
            if (objectType && objectId) {
                setEvidenceTarget({
                    mode: 'object',
                    objectType,
                    objectId,
                    area: risk?.scopeKeys?.[0] || null,
                });
                return;
            }
            setEvidenceTarget({
                mode: 'scope',
                area: risk?.scopeKeys?.[0] || 'technology_architecture',
            });
            return;
        }
        if (key === 'create_irl') {
            setSidebarContent(text);
            closeOverlays();
            setShowIrlSidebar(true);
            return;
        }
        if (key === 'add_to_raid' || key === 'add_raid') {
            setSidebarContent(text);
            closeOverlays();
            setShowRaidSidebar(true);
            return;
        }
        if (key === 'open_raid_item' || key === 'open_raid') {
            closeOverlays();
            const path = buildNavigatePath(
                {
                    route: target.route || '/raid',
                    target: {
                        type: 'raid',
                        id: target.raid_item_id || target.id,
                    },
                },
                projectId,
            );
            navigate(path || '/raid');
            return;
        }
        if (key === 'create_observation') {
            setSidebarContent(text);
            closeOverlays();
            setShowObsSidebar(true);
            return;
        }
        if (key === 'save_to_journal') {
            try {
                await saveInsightsModuleToJournal({
                    dispatch,
                    notesState,
                    moduleLabel: 'Risks & Signals',
                    headline: risk?.title || '',
                    bodyText: buildInsightsItemJournalBody(risk) || text,
                });
                toast.success('Saved to Journal');
            } catch {
                toast.error('Could not save to Journal');
            }
            return;
        }
    };

    if (error || !data || data.isEmpty) {
        const failed =
            Boolean(error) ||
            data?.status === 'failed' ||
            data?.emptyState?.type === 'failed';
        return (
            <InsightsModuleChrome
                title={data?.title || 'Risks & Signals'}
                subtitle={
                    data?.subtitle ||
                    'Material technology risks, potential impacts and warning indicators.'
                }
                metaLine={data?.metaLine || 'Entire Project'}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                <ModuleEmptyState
                    moduleKey="risks_signals"
                    loading={Boolean(loading && !error)}
                    error={error}
                    emptyState={data?.emptyState}
                    status={data?.status}
                    variant={failed ? 'failed' : 'not_generated'}
                    generating={generating}
                    onGoToOverview={onGoToOverview}
                    onGenerateAnalysis={onGenerateAnalysis}
                    onRetry={onRetry}
                />
            </InsightsModuleChrome>
        );
    }

    const distribution = data.distribution || { critical: 0, high: 0, medium: 0, low: 0 };
    const distTotal =
        distribution.critical + distribution.high + distribution.medium + distribution.low || 1;
    const filterOptions = data.filters?.options || {};
    const scopeOptions =
        filterOptions.scope?.length > 1
            ? filterOptions.scope
            : [
                  'all',
                  ...Array.from(
                      new Set((data.risks || []).flatMap(r => r.scopes || [])),
                  ).sort(),
              ];

    const filteredRisks = (data.risks || []).filter(risk => {
        if (
            severityFilter !== 'all' &&
            String(risk.severity).toLowerCase() !== severityFilter.toLowerCase()
        ) {
            return false;
        }
        if (scopeFilter !== 'all') {
            const scopes = risk.scopes || [];
            if (!scopes.some(s => String(s).toLowerCase() === scopeFilter.toLowerCase())) {
                return false;
            }
        }
        if (statusFilter !== 'all') {
            const statusKey = String(risk.statusKey || risk.status || '')
                .toLowerCase()
                .replace(/\s+/g, '_');
            if (statusKey !== statusFilter.toLowerCase().replace(/\s+/g, '_')) {
                return false;
            }
        }
        if (
            confidenceFilter !== 'all' &&
            String(risk.confidence || '').toLowerCase() !== confidenceFilter.toLowerCase()
        ) {
            return false;
        }
        return true;
    });

    return (
        <>
            <InsightsModuleChrome
                title={data.title}
                subtitle={data.subtitle}
                metaLine={data.metaLine}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}
                demoBadge={false}>
                <section className={styles.riskAttentionCard}>
                    {data.attentionBanner ? (
                        <div className={styles.riskAttentionBanner}>
                            <Warning12Filled aria-hidden />
                            <span>{data.attentionBanner}</span>
                        </div>
                    ) : null}
                    <div className={styles.riskDistBar} aria-hidden>
                        <div
                            className={styles.riskDistCritical}
                            style={{
                                width: `${(distribution.critical / distTotal) * 100}%`,
                            }}
                        />
                        <div
                            className={styles.riskDistHigh}
                            style={{ width: `${(distribution.high / distTotal) * 100}%` }}
                        />
                        <div
                            className={styles.riskDistMedium}
                            style={{
                                width: `${(distribution.medium / distTotal) * 100}%`,
                            }}
                        />
                        <div
                            className={styles.riskDistLow}
                            style={{ width: `${(distribution.low / distTotal) * 100}%` }}
                        />
                    </div>
                    <div className={styles.riskDistLegend}>
                        <span className={`${styles.riskDistItem} ${styles.riskDistItemCritical}`}>
                            <ErrorCircle12Filled aria-hidden /> {distribution.critical} Critical
                        </span>
                        <span className={`${styles.riskDistItem} ${styles.riskDistItemHigh}`}>
                            <Warning12Filled aria-hidden /> {distribution.high} High
                        </span>
                        <span className={`${styles.riskDistItem} ${styles.riskDistItemMedium}`}>
                            <Circle12Regular aria-hidden /> {distribution.medium} Medium
                        </span>
                        <span className={`${styles.riskDistItem} ${styles.riskDistItemLow}`}>
                            <SubtractCircle12Regular aria-hidden /> {distribution.low} Low
                        </span>
                    </div>
                </section>

                <div className={styles.filters}>
                    <div className={styles.filterGroup}>
                        <span className={styles.filterLabel}>Severity:</span>
                        <select
                            className={styles.filterSelect}
                            value={severityFilter}
                            onChange={e => setSeverityFilter(e.target.value)}
                            aria-label="Severity filter">
                            {(filterOptions.severity || ['all', 'critical', 'high', 'medium', 'low']).map(
                                opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'All' : titleCase(opt)}
                                    </option>
                                ),
                            )}
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <span className={styles.filterLabel}>Scope:</span>
                        <select
                            className={styles.filterSelect}
                            value={scopeFilter}
                            onChange={e => setScopeFilter(e.target.value)}
                            aria-label="Scope filter">
                            {scopeOptions.map(opt => (
                                <option key={opt} value={opt}>
                                    {opt === 'all' ? 'All' : opt}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <span className={styles.filterLabel}>Status:</span>
                        <select
                            className={styles.filterSelect}
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                            aria-label="Status filter">
                            {(
                                filterOptions.status || [
                                    'all',
                                    'open',
                                    'in_progress',
                                    'mitigated',
                                ]
                            ).map(opt => (
                                <option key={opt} value={opt}>
                                    {opt === 'all' ? 'All' : titleCase(opt)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <span className={styles.filterLabel}>Confidence:</span>
                        <select
                            className={styles.filterSelect}
                            value={confidenceFilter}
                            onChange={e => setConfidenceFilter(e.target.value)}
                            aria-label="Confidence filter">
                            {(
                                filterOptions.confidence || ['all', 'high', 'medium', 'low']
                            ).map(opt => (
                                <option key={opt} value={opt}>
                                    {opt === 'all' ? 'All' : titleCase(opt)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <section className={styles.riskTable}>
                    <div className={styles.riskTableHead}>
                        <span>Risk</span>
                        <span>Scope</span>
                        <span>Severity</span>
                        <span>Confidence</span>
                        <span>Status</span>
                        <span>Action</span>
                    </div>
                    {filteredRisks.length ? (
                        filteredRisks.map(risk => {
                            const action = firstSupportedInsightsAction(
                                [
                                    risk.action,
                                    ...(risk.actions || []),
                                    { key: 'view_details', label: 'View →' },
                                ],
                                { moduleKey: 'risks_signals', context: { item: risk } },
                            );
                            return (
                                <div
                                    key={risk.id}
                                    role="button"
                                    tabIndex={0}
                                    className={`${styles.riskTableRow} ${
                                        detailRisk?.id === risk.id ? styles.riskTableRowActive : ''
                                    }`}
                                    onClick={() => setDetailRisk(risk)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setDetailRisk(risk);
                                        }
                                    }}>
                                    <p className={styles.riskTitleCell}>{risk.title}</p>
                                    <div>
                                        {(risk.scopes || []).map(scope => (
                                            <span key={scope} className={styles.scopeChip}>
                                                {scope}
                                            </span>
                                        ))}
                                    </div>
                                    <span
                                        className={`${styles.severityCell} ${severityClass(
                                            risk.severity,
                                        )}`}>
                                        <SeverityIcon severity={risk.severity} />
                                        {titleCase(risk.severity)}
                                    </span>
                                    <p className={styles.confidenceCell}>{risk.confidence}</p>
                                    <span className={statusChipClass(risk.statusKey)}>
                                        {risk.status}
                                    </span>
                                    <span>
                                        {action ? (
                                            <button
                                                type="button"
                                                className={styles.btnOutline}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleAction(action, risk);
                                                }}>
                                                {action.label}
                                            </button>
                                        ) : null}
                                    </span>
                                </div>
                            );
                        })
                    ) : (
                        <p className={styles.emptyNote} style={{ padding: 16 }}>
                            {data.emptyRegister
                                ? data.emptyRegisterMessage ||
                                  'No material canonical risks were identified in this analysis version.'
                                : 'No risks match the current filters.'}
                        </p>
                    )}
                </section>
            </InsightsModuleChrome>

            <RiskDetailDrawer
                open={Boolean(detailRisk)}
                risk={detailRisk}
                onClose={() => setDetailRisk(null)}
                onAction={handleAction}
                onOpenModule={onOpenModule}
            />

            <HighlightsEvidencePanel
                open={Boolean(evidenceTarget)}
                projectId={projectId}
                area={evidenceTarget?.area || null}
                objectType={evidenceTarget?.objectType || null}
                objectId={evidenceTarget?.objectId || null}
                endpointMode={evidenceTarget?.mode === 'object' ? 'object' : 'scope'}
                onClose={() => setEvidenceTarget(null)}
                onOpenModule={onOpenModule}
                onAction={(action, panelCard) => {
                    void handleAction(action, {
                        title: panelCard?.headline || detailRisk?.title,
                        body: panelCard?.conclusion || detailRisk?.body,
                    });
                }}
            />

            <PostInformationRequestSidebar
                show={showIrlSidebar}
                setShow={show => {
                    setShowIrlSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultRequest={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={sidebarContent || ''}
            />
        </>
    );
};

export default IntelligenceInsightsRisksSignals;
